import _get from 'lodash/get'
import React from 'react'
import { useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Pressable, Text } from 'react-native'
import { z } from 'zod'

import { useFlowsServicesResponses } from '@flows-ui/services/FlowServicesResponsesContext'
import { processStepContentElementText } from '@flows-ui/utils/processStepContentElementText'
import type { TextContent } from '@flows/core/i18n/types'
import { textContentSchema } from '@flows/core/i18n/types'
import type { AnyCallbackConfig } from '@flows/core/types/dispatchedHookTypes'
import type { DropDown as DropDownProps } from '@flows/core/types/stepContentTypes'
import type { AnySimpleOnSubmitHook, FlowValues } from '@flows/core/types/stepTypes'
import Select from '@ui-kit/components/formComponents/Select'
import { body2 } from '@ui-kit/core/typography'

interface Props<
  TCallbackConfig extends AnyCallbackConfig,
  TSimpleOnSubmitHook extends AnySimpleOnSubmitHook,
> extends DropDownProps<TCallbackConfig, TSimpleOnSubmitHook> {
  flowValues: FlowValues
}

const optionSchema = z.array(
  z.object({
    value: z.union([z.string(), z.number(), z.boolean()]),
    textValue: z.string().optional(),
    label: textContentSchema,
  })
)

const getMappedOptions = <TProps extends Props<any, any>>(props: TProps) => {
  if (typeof props.options === 'string') {
    const refinedOptions = z.array(z.any()).safeParse(_get(props.flowValues, props.options))
    if (!refinedOptions.success) {
      return []
    }
    return optionSchema.parse(
      props.optionsTransformer ? props.optionsTransformer(refinedOptions.data) : refinedOptions.data
    )
  }

  return optionSchema.parse(
    props.optionsTransformer ? props.optionsTransformer(props.options) : props.options
  )
}

const isEmptyValue = (value: unknown) => value === undefined || value === null || value === ''

const useCollapsedLabel = (
  collapsedLabel: TextContent | undefined,
  value: unknown,
  hasError: boolean
) => {
  const [isRevealed, setIsRevealed] = React.useState(false)
  const isCollapsed = !isRevealed && !hasError && isEmptyValue(value)
  return {
    collapsedLabel: isCollapsed ? collapsedLabel : undefined,
    reveal: () => setIsRevealed(true),
  }
}

export const DropDown = <TProps extends Props<any, any>>(props: TProps) => {
  const { t } = useTranslation()
  const servicesResponses = useFlowsServicesResponses()
  const translateFunction = processStepContentElementText(t, {
    flowValues: props.flowValues,
    servicesResponses,
  })
  const { field, fieldState } = useController({
    name: props.name,
  })
  const { value: fieldValue, onChange: onFieldChange } = field
  const disabledConfig = props.disabledConditionConfig
  const isDisabled =
    disabledConfig !== undefined &&
    String(_get(props.flowValues, disabledConfig.condition.valuePath)) ===
      String(disabledConfig.condition.expectedValue)
  const hasForcedFallbackValue = React.useRef(false)
  const { collapsedLabel, reveal } = useCollapsedLabel(
    props.collapsedLabel,
    fieldValue,
    fieldState.error !== undefined
  )

  React.useEffect(() => {
    if (!disabledConfig) {
      return
    }
    if (isDisabled) {
      hasForcedFallbackValue.current = true
      if (fieldValue !== disabledConfig.fallbackValue) {
        onFieldChange(disabledConfig.fallbackValue)
      }
    }
    if (!isDisabled && hasForcedFallbackValue.current) {
      hasForcedFallbackValue.current = false
      onFieldChange(undefined)
    }
  }, [disabledConfig, isDisabled, fieldValue, onFieldChange])
  const options = getMappedOptions(props).map((opt) => ({
    value: opt.value,
    label: translateFunction(opt.label),
  }))
  const label = translateFunction(props.label)

  if (collapsedLabel) {
    return (
      <Pressable
        accessibilityRole="button"
        testID={`dropDown_${props.name}_reveal`}
        onPress={reveal}
      >
        <Text className={body2({ color: 'grey700' })}>{translateFunction(collapsedLabel)}</Text>
      </Pressable>
    )
  }

  return (
    <Select
      optionId={`dropDown_${props.name}`}
      testID={`dropDown_${props.name}`}
      label={label}
      name={props.name}
      value={field.value}
      styleType={props.styleType}
      options={options}
      useFilterInput={props.useFilterInput !== false}
      onValueChange={field.onChange}
      disabled={isDisabled}
      status={fieldState.error?.message ? 'error' : undefined}
      errorMessage={fieldState.error?.message}
    />
  )
}
